import moment from "moment";
import { Button, List, Space, Empty } from "antd";
import { useEffect, useState } from "react";
import { App, Component } from "../../types";
import { getKubeService } from "../../backend";
import { useParams } from "react-router";
import { GetMicroservicePodEventsResponse } from "@microtica/ms-kube-sdk";
import { Check, Clock, Container, AlertTriangle } from "lucide-react";
import AlarmSeverityTag from "../../components/alarms/AlarmSeverityTag";

function getSeverityColor(severity: string): string {
    switch (severity) {
        case "Critical":
        case "High":
        case "Error":
            return "#ef4444";
        case "Medium":
        case "Warning":
            return "#f59e0b";
        case "Low":
            return "#3b82f6";
        case "Normal":
            return "#059669";
        default:
            return "#9ca3af";
    }
}

interface AppIncidentsProps {
    component: Component;
    app: App;
}

interface IncidentsState {
    id: string;
    timestamp: number;
    severity: "Critical" | "High" | "Medium" | "Low" | "Warning" | "Normal";
    code?: string;
    description: string;
    type: "event" | "alarm" | string;
}

const AppIncidents = ({ component, app }: AppIncidentsProps) => {
    const { projectId } = useParams() as { projectId: string; }
    const [loading, setLoading] = useState(true);
    const [incidents, setIncidents] = useState<IncidentsState[]>([]);


    useEffect(() => {
        loadData();

        const interval = setInterval(() => {
            loadData();
        }, 30000);

        return () => { clearInterval(interval); }
    }, []);

    const loadData = async () => {
        const { data: { pods } } = await getKubeService().getMicroserviceDeploymentStatus(
            app.name,
            component.clusterId!,
            app.namespace,
            projectId
        );
        const podsNames = pods.map(p => p.metadata.name);
        const podsEvents = await Promise.all(podsNames.map(podName =>
            getKubeService().getMicroservicePodEvents(
                app.name,
                app.namespace,
                component.clusterId!,
                podName,
                projectId
            )
        )) as { data: GetMicroservicePodEventsResponse }[];

        setIncidents(
            podsEvents
                .reduce((allEvents, res) => {
                    const events = res.data.events;
                    allEvents.push(...events.map(e => {
                        // Map Kubernetes event types to severity
                        let severity: "Critical" | "High" | "Medium" | "Low" | "Warning" | "Normal" = "Normal";
                        const eventType = e.type?.toUpperCase();
                        if (eventType === "WARNING") {
                            severity = "Warning";
                        } else if (eventType === "NORMAL") {
                            severity = "Normal";
                        } else {
                            // Fallback: try to map from existing severity values
                            severity = e.type as any || "Normal";
                        }
                        
                        // Handle count display only if lastSeen is valid
                        let countText = "";
                        if ((e.count || 0) > 1 && e.lastSeen && e.lastSeen > 0 && e.firstSeen && e.firstSeen > 0) {
                            countText = ` (x${e.count} ${moment(e.lastSeen).from(e.firstSeen)})`;
                        } else if ((e.count || 0) > 1) {
                            countText = ` (x${e.count})`;
                        }
                        
                        return {
                            id: "",
                            timestamp: e.firstSeen || 0, // Use 0 if firstSeen is missing/invalid
                            severity,
                            code: "",
                            description: `${e.message || ""}${countText}`,
                            type: "event"
                        };
                    }));

                    return allEvents;
                }, [] as IncidentsState[])
                .sort((a, b) => {
                    // Sort invalid timestamps (0) to the end
                    if (a.timestamp === 0 && b.timestamp === 0) return 0;
                    if (a.timestamp === 0) return 1;
                    if (b.timestamp === 0) return -1;
                    return b.timestamp - a.timestamp;
                })
        );
        setLoading(false);
    }

    const handleMarkAsResolved = (id: string) => {

    }

    return (
        <Space direction="vertical" size="large" className="flex-justify-space-between">
            <div className="flex-justify-space-between flex-align-center">
                <div style={{ fontWeight: 600, fontSize: 16 }}>
                    Application Events
                    <div className="gray-text" style={{ fontWeight: 400, fontSize: 14 }}>
                        Recent app events and active alarms, gaining real-time insights into your application's health and performance, with details on critical occurrences and potential issues.
                    </div>
                </div>
            </div>
            <List
                loading={loading}
                bordered={false}
                style={{ 
                    overflow: "auto", 
                    height: "calc(100vh - 404px)",
                    backgroundColor: "transparent"
                }}
                locale={{
                    emptyText:
                        <Empty
                            image={Empty.PRESENTED_IMAGE_SIMPLE}
                            description={
                                <>
                                    <div>
                                        Currently, there are no events or active alarms for your application.
                                    </div>
                                    <div>
                                        While it's normal not to see any events, it's also a positive indication of your application's stability and health.
                                    </div>
                                </>
                            }
                        />
                }}
                dataSource={incidents}
                renderItem={(item) => {
                    const severityColor = getSeverityColor(item.severity);
                    const isAlarm = item.type === "alarm";
                    
                    return (
                        <List.Item
                            style={{
                                borderLeft: `4px solid ${severityColor}`,
                                padding: "16px 20px",
                                marginBottom: "8px",
                                borderRadius: "8px",
                                backgroundColor: "#ffffff",
                                border: "1px solid #e5e7eb",
                                transition: "all 0.2s ease",
                                cursor: "default"
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.boxShadow = "0 2px 8px rgba(0, 0, 0, 0.08)";
                                e.currentTarget.style.borderColor = "#d1d5db";
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.boxShadow = "none";
                                e.currentTarget.style.borderColor = "#e5e7eb";
                            }}
                            actions={isAlarm ? [
                                <Button
                                    key="resolve"
                                    icon={<Check size={14} />}
                                    size="small"
                                    type="text"
                                    onClick={() => handleMarkAsResolved(item.id)}
                                    style={{ 
                                        color: "#6b7280",
                                        opacity: 0.7
                                    }}
                                />
                            ] : undefined}
                        >
                            <div style={{ 
                                display: "flex", 
                                alignItems: "flex-start", 
                                gap: "16px",
                                width: "100%"
                            }}>
                                {/* Icon with colored background */}
                                <div style={{
                                    width: "44px",
                                    height: "44px",
                                    borderRadius: "10px",
                                    backgroundColor: `${severityColor}12`,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    flexShrink: 0,
                                    border: `1.5px solid ${severityColor}25`
                                }}>
                                    {isAlarm ? (
                                        <AlertTriangle size={20} color={severityColor} />
                                    ) : (
                                        <Container size={22} color={severityColor} />
                                    )}
                                </div>

                                {/* Content */}
                                <div style={{ 
                                    flex: 1, 
                                    minWidth: 0,
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: "8px"
                                }}>
                                    {/* Header row with timestamp and badge */}
                                    <div style={{ 
                                        display: "flex", 
                                        alignItems: "center", 
                                        justifyContent: "space-between",
                                        flexWrap: "wrap",
                                        gap: "12px"
                                    }}>
                                        {item.timestamp > 0 ? (
                                            <Space style={{ fontSize: "12px", color: "#6b7280" }}>
                                                <Clock size={13} />
                                                <span style={{ fontWeight: 500 }}>
                                                    {moment(item.timestamp).fromNow()}
                                                </span>
                                                <span style={{ color: "#d1d5db" }}>•</span>
                                                <span>
                                                    {moment(item.timestamp).format("HH:mm • DD MMM YY")}
                                                </span>
                                            </Space>
                                        ) : (
                                            <Space style={{ fontSize: "12px", color: "#9ca3af", fontStyle: "italic" }}>
                                                <Clock size={13} />
                                                <span>Timestamp unavailable</span>
                                            </Space>
                                        )}
                                        <AlarmSeverityTag severity={item.severity} />
                                    </div>

                                    {/* Description */}
                                    <div style={{ 
                                        fontSize: "14px", 
                                        color: "#111827", 
                                        lineHeight: "1.6",
                                        wordBreak: "break-word"
                                    }}>
                                        {item.code && (
                                            <span style={{ 
                                                fontWeight: 600, 
                                                marginRight: "10px",
                                                color: severityColor,
                                                fontSize: "12px",
                                                textTransform: "uppercase",
                                                letterSpacing: "0.05em",
                                                padding: "2px 6px",
                                                borderRadius: "4px",
                                                backgroundColor: `${severityColor}10`
                                            }}>
                                                {item.code}
                                            </span>
                                        )}
                                        <span>{item.description}</span>
                                    </div>
                                </div>
                            </div>
                        </List.Item>
                    );
                }}
            />
        </Space>
    )
}

export default AppIncidents;